import type { Config as DevelopmentConfig } from "./config.development";
import type { Config as ProductionConfig } from "./config.production";
import type { Config as StagingConfig } from "./config.staging";
import type { Config as TestConfig } from "./config.test-env";

// The pattern is to put configuration that is applicable to all environments here
// and then override it in the environment-specific config files.
//
// DO NOT PUT SECRETS IN HERE!!!
const createCommonConfig = (
  config: TestConfig | DevelopmentConfig | StagingConfig | ProductionConfig,
) => ({
  flags: {
    enablePseudoLocale: false,
  },
  releaseDates: {
    privacyPolicy: "03/26/2024",
  } as Record<string, string>,
  policyVersions: {
    dataProcessingAgreement: "03/25/2024",
  },
  tracking: {
    DEBUG: false,
    ENABLED: true,
    MIXPANEL_HOST: "https://moxy.read.ai",
    MIXPANEL_VERBOSE_LOG: false,
    GOOGLE_CONVERSION_ID: null,
    LINKED_IN_PARTNER_ID: null,
  },
  billing: {
    maximumPurchaseAmount: 99999999, // $999,999.99 in cents
  },
  auth: {
    apple: {
      AUTH_URL: `${config.api.HOST}/login/apple`,
    },
    google_one_tap: {
      AUTH_URL: `${config.api.HOST}/auth/google_one_tap`,
    },
    google: {
      AUTH_URL: `${config.api.HOST}/login/google`,
    },
    google_calendar: {
      AUTH_URL: `${config.api.HOST}/calendar-connect/google`,
      ENABLE_URL: `${config.api.HOST}/calendar-connection/google`,
    },
    zoom: {
      AUTH_URL: `${config.api.HOST}/login/zoom`,
    },
    zoom_calendar: {
      AUTH_URL: `${config.api.HOST}/zoomapp/install?connect_calendar=True`,
    },
    webex: {
      AUTH_URL: `${config.api.HOST}/login/webex`,
    },
    microsoft_graph: {
      AUTH_URL: `${config.api.HOST}/login/microsoft`,
    },
    microsoft_graph_calendar: {
      AUTH_URL: `${config.api.HOST}/calendar-connect/microsoft`,
      ENABLE_URL: `${config.api.HOST}/calendar-connection/microsoft`,
    },
    okta: {
      AUTH_URL: `${config.api.HOST}/login/okta`,
    },
    slack_sso: {
      AUTH_URL: `${config.api.HOST}/login/slack`,
    },
  },
  calendar: {
    GOOGLE_URL: `${config.api.HOST}/calendar-connect/google`,
    MICROSOFT_URL: `${config.api.HOST}/calendar-connect/microsoft`,
    ZOOM_URL: `${config.api.HOST}/zoomapp/install?connect_calendar=True`,
  },
  integrations: {
    confluence: {
      AUTH_URL: `${config.api.HOST}/integrations/confluence/connect`,
      CONNECTION_URL: `${config.api.HOST}/integrations/confluence/connection`,
    },
    google_contacts: {
      AUTH_URL: `${config.api.HOST}/integrations/google-contacts/connect`,
      CONNECTION_URL: `${config.api.HOST}/integrations/google-contacts/connection`,
    },
    google_docs: {
      AUTH_URL: `${config.api.HOST}/integrations/google-docs/connect`,
      CONNECTION_URL: `${config.api.HOST}/integrations/google-docs/connection`,
    },
    google_drive: {
      AUTH_URL: `${config.api.HOST}/integrations/google-drive/connect`,
      CONNECTION_URL: `${config.api.HOST}/integrations/google-drive/connection`,
    },
    google_email: {
      AUTH_URL: `${config.api.HOST}/integrations/google-email/connect`,
      CONNECTION_URL: `${config.api.HOST}/integrations/google-email/connection`,
    },
    google_meet: {
      AUTH_URL: `${config.api.HOST}/integrations/google-meet/connect`,
      CONNECTION_URL: `${config.api.HOST}/integrations/google-meet/connection`,
    },
    hubspot: {
      AUTH_URL: `${config.api.HOST}/integrations/hubspot/connect`,
      CONNECTION_URL: `${config.api.HOST}/integrations/hubspot/connection`,
    },
    jira: {
      AUTH_URL: `${config.api.HOST}/integrations/jira/connect`,
      CONNECTION_URL: `${config.api.HOST}/integrations/jira/connection`,
    },
    microsoft_graph_contacts: {
      AUTH_URL: `${config.api.HOST}/integrations/microsoft-graph-contacts/connect`,
      CONNECTION_URL: `${config.api.HOST}/integrations/microsoft-graph-contacts/connection`,
    },
    microsoft_graph_email: {
      AUTH_URL: `${config.api.HOST}/integrations/microsoft-email/connect`,
      CONNECTION_URL: `${config.api.HOST}/integrations/microsoft-email/connection`,
    },
    microsoft_graph_onenote: {
      AUTH_URL: `${config.api.HOST}/integrations/microsoft-onenote/connect`,
      CONNECTION_URL: `${config.api.HOST}/integrations/microsoft-onenote/connection`,
    },
    microsoft_graph_teams_chat_ingest: {
      AUTH_URL: `${config.api.HOST}/integrations/microsoft-teams-ingest/connect`,
      CONNECTION_URL: `${config.api.HOST}/integrations/microsoft-teams-ingest/connection`,
    },
    microsoft_graph_teams_push: {
      AUTH_URL: `${config.api.HOST}/integrations/microsoft-teams-push/connect`,
      CONNECTION_URL: `${config.api.HOST}/integrations/microsoft-teams-push/connection`,
    },
    notion: {
      AUTH_URL: `${config.api.HOST}/integrations/notion/connect`,
      CONNECTION_URL: `${config.api.HOST}/integrations/notion/connection`,
    },
    salesforce: {
      AUTH_URL: `${config.api.HOST}/integrations/salesforce/connect`,
      CONNECTION_URL: `${config.api.HOST}/integrations/salesforce/connection`,
    },
    slack_bot: {
      AUTH_URL: `${config.api.HOST}/integrations/slack/connect`,
      REMOVE_URL:
        "https://slack.com/apps/A04UUCUGESK-read-ai?tab=settings&next_id=0",
    },
    slack_ingest: {
      AUTH_URL: `${config.api.HOST}/integrations/slack-ingest/connect`,
      CONNECTION_URL: `${config.api.HOST}/integrations/slack-ingest/connection`,
    },
    zapier: {
      CONFIGURE_URL: "https://zapier.com/app/zaps",
    },
  },
});

export default createCommonConfig;
